import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { authApi } from '../services/api';
import { useGlobalLoader } from '../components/TimchehLoaderProvider';
import PasswordStrengthIndicator from '../components/PasswordStrengthIndicator';
import { validatePasswordStrength } from '../utils/passwordValidation';
import toast from 'react-hot-toast';

interface ResetPasswordForm {
  newPassword: string;
  confirmPassword: string;
}

const ResetPasswordPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const { showLoader, hideLoader } = useGlobalLoader();
  const navigate = useNavigate();
  const [isSuccess, setIsSuccess] = useState(false);

  useEffect(() => {
    if (!token) {
      toast.error('لینک بازنشانی نامعتبر است');
      navigate('/forgot-password');
    }
  }, [token, navigate]);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting }
  } = useForm<ResetPasswordForm>();

  const newPassword = watch('newPassword');

  const onSubmit = async (data: ResetPasswordForm) => {
    if (!token) return;

    try {
      showLoader({ message: 'در حال بازنشانی رمز عبور...', fullScreen: true });
      
      await authApi.resetPassword(token, data.newPassword);
      
      hideLoader();
      setIsSuccess(true);
      toast.success('رمز عبور با موفقیت بازنشانی شد');
      
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (error: any) {
      hideLoader();
      const errorMessage = error.response?.data?.message;
      toast.error(errorMessage || 'بازنشانی رمز عبور ناموفق بود. لینک ممکن است منقضی شده باشد.');
    }
  };

  if (!token) {
    return null;
  }

  if (isSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8" dir="rtl">
        <div className="max-w-md w-full space-y-8 text-center">
          <div className="bg-green-50 border border-green-200 rounded-md p-6">
            <h2 className="text-2xl font-extrabold text-green-800 mb-2">
              بازنشانی رمز عبور موفق بود
            </h2>
            <p className="text-sm text-green-700 mb-4">
              رمز عبور شما با موفقیت بازنشانی شد. در حال انتقال به صفحه ورود...
            </p>
            <Link
              to="/login"
              className="text-sm font-medium text-primary-600 hover:text-primary-500"
            >
              ← رفتن به ورود
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8" dir="rtl">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            بازنشانی رمز عبور
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            رمز عبور جدید خود را وارد کنید
          </p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit(onSubmit)}>
          <div>
            <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700">
              رمز عبور جدید
            </label>
            <input
              {...register('newPassword', {
                required: 'رمز عبور الزامی است',
                validate: (value) => {
                  if (!value) return true;
                  return validatePasswordStrength(value) || 'رمز عبور الزامات امنیتی را برآورده نمی‌کند';
                }
              })}
              type="password"
              className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm"
              placeholder="یک رمز عبور قوی وارد کنید"
            />
            {errors.newPassword && (
              <p className="mt-1 text-sm text-red-600">{errors.newPassword.message}</p>
            )}
            <PasswordStrengthIndicator password={newPassword || ''} />
          </div>

          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
              تأیید رمز عبور
            </label>
            <input
              {...register('confirmPassword', {
                required: 'لطفاً رمز عبور خود را تأیید کنید',
                validate: value => value === newPassword || 'رمزهای عبور مطابقت ندارند'
              })}
              type="password"
              className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm"
              placeholder="رمز عبور جدید را تأیید کنید"
            />
            {errors.confirmPassword && (
              <p className="mt-1 text-sm text-red-600">{errors.confirmPassword.message}</p>
            )}
          </div>

          <div>
            <button
              type="submit"
              disabled={isSubmitting}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'در حال بازنشانی...' : 'بازنشانی رمز عبور'}
            </button>
          </div>

          <div className="text-center">
            <Link
              to="/login"
              className="text-sm font-medium text-primary-600 hover:text-primary-500"
            >
              بازگشت به ورود ←
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ResetPasswordPage;

